import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useHistory, useParams } from "react-router";
import ServiceWrapper from "../../components/ServiceWrapper";
import { api } from "../../utils/api";
import { track } from "../../utils/track";
import { C, Page, Lead, Primary, Ghost, Field, Err, Note, errText } from "./styles";

/** 악보 한 줄 = 4마디. 마디마다 코드 칸 4개(박자) + 가사 1칸 — 서버 rows(chord_1..16, lyrics_1..4)와 같은 모양 */
type Row = { [k: string]: string | null | undefined };

const BARS = [0, 1, 2, 3];
const BEATS = [1, 2, 3, 4];

const emptyRow = (): Row => ({});

/** 내 악보 고치기 (/my-scores/:id/edit): 제목·가수·키, 코드 칸·가사 칸, 줄 추가·삭제 */
const MyScoreEdit: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [title, setTitle] = useState("");
  const [singer, setSinger] = useState("");
  const [key, setKey] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/my-scores/${id}`).then(({ data }) => {
      const d = data.data;
      if (!d.my.is_owner || d.my.status !== "done") { history.replace(`/my-scores/${id}`); return; }
      setTitle(d.info.title);
      setSinger(d.info.singer || "");
      setKey(d.info.original_key || "");
      setRows(d.musics.map((m: Row) => ({ ...m })));
    }).catch((e) => setErr(errText(e, "악보를 불러오지 못했어요.")));
  }, [id, history]);

  // 저장 안 하고 나가려 하면 한 번 묻기
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const set = (ri: number, k: string, v: string) => {
    if (!rows) return;
    const next = rows.slice();
    next[ri] = { ...next[ri], [k]: v };
    setRows(next);
    setDirty(true);
  };
  const addRow = (at: number) => {
    if (!rows) return;
    const next = rows.slice();
    next.splice(at, 0, emptyRow());
    setRows(next);
    setDirty(true);
  };
  const delRow = (ri: number) => {
    if (!rows || !window.confirm(`${ri + 1}번째 줄을 지울까요?`)) return;
    setRows(rows.filter((_, i) => i !== ri));
    setDirty(true);
  };

  const save = async () => {
    if (!rows || !title.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const clean = rows.map((r) => {
        const o: Row = {};
        for (let k = 1; k <= 16; k++) o[`chord_${k}`] = (r[`chord_${k}`] || "").trim();
        for (let k = 1; k <= 4; k++) o[`lyrics_${k}`] = r[`lyrics_${k}`] || "";
        return o;
      });
      await api.post(`/my-scores/${id}`, { title: title.trim(), singer: singer.trim(), original_key: key.trim(), rows: clean });
      track("myscore:save");
      setDirty(false);
      history.replace(`/my-scores/${id}`);
    } catch (e) {
      setErr(errText(e, "저장하지 못했어요. 잠시 뒤 다시 눌러 주세요."));
    }
    setBusy(false);
  };

  const leave = () => {
    if (dirty && !window.confirm("고친 내용을 저장하지 않고 나갈까요?")) return;
    setDirty(false);
    history.replace(`/my-scores/${id}`);
  };

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <h1>악보 고치기</h1>
        <Lead>칸을 눌러 코드나 가사를 바꿔 주세요. 한 줄은 4마디, 마디마다 코드 칸 4개는 1·2·3·4 박이에요.</Lead>
        {err && <Err>{err}</Err>}
        {rows && (
          <>
            <Meta>
              <Field><span>곡 제목</span><input value={title} maxLength={100} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} /></Field>
              <div>
                <Field><span>가수·연주자</span><input value={singer} maxLength={100} onChange={(e) => { setSinger(e.target.value); setDirty(true); }} /></Field>
                <Field className="key"><span>키</span><input value={key} maxLength={10} onChange={(e) => { setKey(e.target.value); setDirty(true); }} /></Field>
              </div>
            </Meta>

            {rows.map((r, ri) => (
              <RowBox key={ri}>
                <header>
                  <span>{ri + 1}줄</span>
                  <div>
                    <button type="button" onClick={() => addRow(ri)}>위에 줄 넣기</button>
                    <button type="button" className="del" onClick={() => delRow(ri)}>줄 지우기</button>
                  </div>
                </header>
                <Bars>
                  {BARS.map((b) => (
                    <div key={b} className="bar">
                      <div className="chords">
                        {BEATS.map((t) => {
                          const k = `chord_${b * 4 + t}`;
                          return (
                            <input
                              key={k}
                              value={r[k] || ""}
                              maxLength={40}
                              aria-label={`${ri + 1}줄 ${b + 1}마디 ${t}박 코드`}
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              onChange={(e) => set(ri, k, e.target.value)}
                            />
                          );
                        })}
                      </div>
                      <input
                        className="lyric"
                        value={(r[`lyrics_${b + 1}`] || "").replace(/ /g, " ")}
                        placeholder="가사"
                        aria-label={`${ri + 1}줄 ${b + 1}마디 가사`}
                        onChange={(e) => set(ri, `lyrics_${b + 1}`, e.target.value)}
                      />
                    </div>
                  ))}
                </Bars>
              </RowBox>
            ))}
            <Ghost type="button" style={{ width: "100%", marginTop: 4 }} onClick={() => addRow(rows.length)}>+ 맨 끝에 줄 넣기</Ghost>
            <Note style={{ marginTop: 12 }}>코드는 C, Am7, F#m, G/B 처럼 적어 주세요. 칸을 비우면 앞 코드가 이어져요.</Note>

            <SaveBar>
              <Ghost type="button" onClick={leave}>취소</Ghost>
              <Primary type="button" disabled={busy || !title.trim() || !dirty} onClick={save}>{busy ? "저장하는 중…" : "저장"}</Primary>
            </SaveBar>
          </>
        )}
        {!rows && !err && <Lead>불러오는 중…</Lead>}
      </Page>
    </ServiceWrapper>
  );
};

export default MyScoreEdit;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  & > div {
    display: flex;
    gap: 10px;
  }
  & > div > label {
    flex: 1;
    min-width: 0;
  }
  & > div > label.key {
    flex: 0 0 84px;
  }
`;

const RowBox = styled.section`
  margin-bottom: 14px;
  & > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    font-size: 13px;
    color: ${C.dim};
  }
  & > header button {
    border: 0;
    background: none;
    color: ${C.link};
    font-size: 13px;
    padding: 4px 0 4px 12px;
    cursor: pointer;
  }
  & > header button.del {
    color: ${C.err};
  }
`;

const Bars = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* 휴대폰: 한 줄에 한 마디 (코드 칸이 좁으면 Am7 같은 코드가 잘림) */
  gap: 6px;
  & .bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
    border-radius: 10px;
    background: ${C.card};
    border: 1px solid ${C.line};
    min-width: 0;
  }
  & .chords {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 3px;
  }
  & input {
    min-width: 0;
    height: 36px;
    box-sizing: border-box;
    padding: 0 4px;
    border-radius: 7px;
    border: 1px solid transparent;
    background: #0d0e12;
    color: ${C.fg};
    font-size: 15px;
    outline: 0;
    font-family: inherit;
  }
  & .chords input {
    text-align: center;
    font-weight: 700;
    color: #8fb0ff;
  }
  & input.lyric {
    font-size: 14px;
    padding: 0 8px;
  }
  & input:focus {
    border-color: ${C.accent};
  }
  @media (min-width: 520px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SaveBar = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding: 12px 0 calc(12px + env(safe-area-inset-bottom, 0px));
  background: #0d0e12;
  & > button:last-of-type {
    flex: 1;
  }
  & > button {
    height: 52px;
  }
`;
